import React, { useState } from 'react';

interface DeletionRequest {
  userId: string;
  email: string;
  reason?: string;
}

const DataDeletion: React.FC = () => {
  const [formData, setFormData] = useState<DeletionRequest>({
    userId: '',
    email: '',
    reason: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(process.env.REACT_APP_PUBLISH_API_URL?.replace('/publish', '/data-deletion') || '/api/data-deletion', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Failed to submit deletion request');
      }

      setIsSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="app">
        <div className="app-main">
          <div className="stage-container">
            <div className="stage-header">
              <h1>Data Deletion Request Submitted</h1>
            </div>
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <div style={{ 
                backgroundColor: '#d4edda', 
                border: '1px solid #c3e6cb', 
                color: '#155724',
                padding: '1rem', 
                borderRadius: '8px',
                marginBottom: '2rem'
              }}>
                <h3>✅ Request Received</h3>
                <p>Your data deletion request has been received and will be processed within 30 days.</p>
                <p>You will receive a confirmation email once the deletion is complete.</p>
              </div>
              
              <h3>What Happens Next?</h3>
              <ul style={{ textAlign: 'left', maxWidth: '600px', margin: '0 auto' }}>
                <li><strong>Processing Time:</strong> 1-30 days</li>
                <li><strong>Email Confirmation:</strong> You'll receive confirmation when deletion is complete</li>
                <li><strong>Complete Deletion:</strong> All account data, racing series data, and published results will be permanently removed</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <div className="app-main">
        <div className="stage-container">
          <div className="stage-header">
            <h1>Data Deletion Request</h1>
            <p>Request complete deletion of your account and all associated data</p>
          </div>

          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <div style={{ 
              backgroundColor: '#f8d7da', 
              border: '1px solid #f5c6cb', 
              color: '#721c24',
              padding: '1rem', 
              borderRadius: '8px',
              marginBottom: '2rem',
              textAlign: 'left'
            }}>
              <h3>⚠️ Warning: Complete Account Deletion</h3>
              <p><strong>This action cannot be undone.</strong> All of the following will be permanently deleted:</p>
              <ul>
                <li>Your account and login credentials</li>
                <li>All racing series data and configurations</li>
                <li>All published results and public pages</li>
                <li>All stored files and backups</li>
              </ul>
              <p><strong>Processing Time:</strong> Requests are processed within 30 days</p>
            </div>

            <form onSubmit={handleSubmit} style={{ textAlign: 'left' }}>
              <div style={{ marginBottom: '1.5rem' }}>
                <label htmlFor="email" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                  Email Address <span style={{ color: '#e74c3c' }}>*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem'
                  }}
                  placeholder="Enter the email address associated with your account"
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label htmlFor="userId" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                  User ID (Optional)
                </label>
                <input
                  type="text"
                  id="userId"
                  value={formData.userId}
                  onChange={(e) => setFormData({ ...formData, userId: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem'
                  }}
                  placeholder="If known, enter your user ID (e.g., A45DHB2)"
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label htmlFor="reason" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                  Reason for Deletion (Optional)
                </label>
                <textarea
                  id="reason"
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    resize: 'vertical'
                  }}
                  placeholder="Optional: Let us know why you're requesting data deletion"
                />
              </div>

              {error && (
                <div style={{ 
                  backgroundColor: '#f8d7da', 
                  border: '1px solid #f5c6cb', 
                  color: '#721c24',
                  padding: '0.75rem', 
                  borderRadius: '8px',
                  marginBottom: '1rem'
                }}>
                  Error: {error}
                </div>
              )}

              <div style={{ textAlign: 'center', marginTop: '2rem' }}>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    backgroundColor: '#e74c3c',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem 2rem',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    opacity: isSubmitting ? 0.6 : 1
                  }}
                >
                  {isSubmitting ? 'Submitting...' : 'Delete My Account Permanently'}
                </button>
              </div>
            </form>

            <hr style={{ margin: '3rem 0', border: '1px solid #e2e8f0' }} />
            
            <div style={{ textAlign: 'left' }}>
              <h2>Frequently Asked Questions</h2>
              
              <h3>What data will be deleted?</h3>
              <ul>
                <li>All personal information and login credentials</li>
                <li>All racing series data and configurations</li>
                <li>All published results and public pages</li>
                <li>All stored files and backups</li>
              </ul>

              <h3>How long does the process take?</h3>
              <p>Data deletion requests are processed within 30 days. You will receive email confirmation once completed.</p>

              <h3>Can I recover my data after deletion?</h3>
              <p>No, data deletion is permanent and cannot be undone. Please make sure you want to delete everything before submitting this request.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataDeletion; 